import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';
import SEO from '../../components/SEO';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Nunito', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 3rem 0;
    text-align: center;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(135deg, rgba(233, 64, 87, 0.05) 0%, rgba(233, 64, 87, 0.1) 100%);
    clip-path: polygon(100% 0, 0% 100%, 100% 100%);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
`;

const Badge = styled.div`
  display: inline-block;
  background-color: rgba(233, 64, 87, 0.1);
  color: var(--primary);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const Heading = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #2D3142;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  span {
    color: var(--primary);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(233, 64, 87, 0.2);
      z-index: -1;
    }
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #6C757D;
  margin-bottom: 2rem;
  max-width: 600px;

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  font-weight: 700;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 15px rgba(233, 64, 87, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(233, 64, 87, 0.4);
    color: white;
  }
`;

const DiscountTag = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #FFAA8A;
  color: #2D3142;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  transform: rotate(15deg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const HeroImageContainer = styled.div`
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  transform: perspective(1000px) rotateY(-5deg);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
`;

const FeatureSection = styled.section`
  padding: 5rem 0;
  background-color: white;
`;

const FeatureCard = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  background-color: rgba(233, 64, 87, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  i {
    font-size: 2rem;
    color: var(--primary);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const FeatureDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
`;

const AISection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, rgba(126, 203, 185, 0.1) 0%, rgba(126, 203, 185, 0.2) 100%);
`;

const AITitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #2D3142;
  text-align: center;
  
  span {
    color: var(--primary);
  }
`;

const AIDescription = styled.p`
  font-size: 1.1rem;
  color: #6C757D;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const AIFeatureCard = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const AIFeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: rgba(233, 64, 87, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  i {
    font-size: 1.5rem;
    color: var(--primary);
  }
`;

const AIFeatureTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #2D3142;
`;

const AIFeatureDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  flex-grow: 1;
`;

const CTASection = styled.section`
  padding: 5rem 0;
  background-color: #2D3142;
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  span {
    color: var(--primary);
  }
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 15px rgba(233, 64, 87, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(233, 64, 87, 0.4);
    color: white;
  }
`;

const LandingPageAI = () => {
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "AI-Powered Baby Sleep Consultant | Personalized Sleep Plans",
    description: "Transform your child's sleep with our AI-powered sleep consultant. Get personalized baby sleep plans based on Australian sleep guidelines.",
    keywords: [
      "AI sleep consultant", 
      "baby sleep AI",
      "personalized sleep plan",
      "sleep training app",
      "baby sleep technology",
      "AI sleep analysis",
      "personalized baby sleep schedule",
      "Australian sleep guidelines"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Dozzy AI Sleep Consultant",
      "description": "AI-powered personalized baby sleep plans based on Australian sleep guidelines",
      "offers": {
        "@type": "Offer",
        "price": "49.00",
        "priceCurrency": "AUD",
        "availability": "https://schema.org/InStock"
      }
    }
  };

  // Google Analytics tracking code setup
  useEffect(() => {
    // Track page view for this landing page
    ReactGA.send({
      hitType: "pageview",
      page: "/landing-ai",
      title: "AI Landing Page"
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      ReactGA.event({
        category: 'engagement',
        action: 'cta_click',
        label: 'AI Landing Page CTA',
        value: 1
      });
    };

    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au/landing-ai"
        ogType="website"
      />
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <HeroContent>
                <Badge>
                  <i className="bi bi-stars me-2"></i>
                  Artificial Intelligence (AI)-Powered Sleep Solutions
                </Badge>
                <Heading>
                  Transform Your Child's Sleep with <span>Personalized AI</span>
                </Heading>
                <SubHeading>
                  Our advanced Artificial Intelligence (AI) analyzes key data points about your child's unique sleep patterns to create a customized sleep plan that evolves as they grow.
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/signup" className="cta-button">
                    Get Your AI Sleep Plan
                  </CtaButton>
                  
                </ButtonWrapper>
                <div className="d-flex align-items-center flex-wrap">
                  <div className="me-4 mb-3">
                    <div className="d-flex align-items-center">
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning me-1"></i>
                      <i className="bi bi-star-fill text-warning"></i>
                    </div>
                    <p className="mb-0 mt-1" style={{ color: '#6C757D' }}>
                      <small>Positive feedback from many parents</small>
                    </p>
                  </div>
                <div className="mb-3">
                    <div className="d-flex align-items-center">
                      <img src="/images/logo-rednose.png" alt="Red Nose Guidelines" height="30" className="me-2" />
                      <img src="/images/logo-triple-p.png" alt="Triple P Resources" height="30" className="me-2" />
                      <img src="/images/logo-raisingchildren.png" alt="Raising Children Network" height="30" className="me-2" />
                      <img src="/images/logo-tresilian.png" alt="Tresillian" height="30" />
                    </div>
                    <p className="mb-0 mt-1" style={{ color: '#6C757D' }}>
                      <small>Evidence-based guidelines</small>
                    </p>
                  </div>
                </div>
              </HeroContent>
            </div>
            <div className="col-lg-6">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/kelly-sikkema-TtsKN6qSf9A-unsplash.jpg" 
                  alt="Peaceful sleeping baby with AI visualization" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <FeatureSection>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">How Our AI Works For You</h2>
            <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
              Our advanced AI technology creates personalized sleep plans that adapt to your child's unique needs
            </p>
          </div>
          
          <div className="row">
            <div className="col-md-4">
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-clipboard-data"></i>
                </FeatureIcon>
                <FeatureTitle>Smart Data Analysis</FeatureTitle>
                <FeatureDescription>
                  Our AI analyzes key data points about your child's sleep patterns, age, development, and environment to create a truly personalized plan.
                </FeatureDescription>
              </FeatureCard>
            </div>
            
            <div className="col-md-4">
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-sliders"></i>
                </FeatureIcon>
                <FeatureTitle>Adaptive Learning</FeatureTitle>
                <FeatureDescription>
                  As your child grows and their sleep needs change, our AI adapts the plan to ensure continued effectiveness and alignment with developmental milestones.
                </FeatureDescription>
              </FeatureCard>
            </div>
            
            <div className="col-md-4">
              <FeatureCard>
                <FeatureIcon>
                  <i className="bi bi-shield-check"></i>
                </FeatureIcon>
                <FeatureTitle>Evidence-Based Approach</FeatureTitle>
                <FeatureDescription>
                  Our AI is trained on guidelines from Australia's most trusted sleep authorities, ensuring all recommendations are safe and effective.
                </FeatureDescription>
              </FeatureCard>
            </div>
          </div>
        </div>
      </FeatureSection>

      <AISection>
        <div className="container">
          <AITitle>
            The <span>AI Advantage</span> for Better Sleep
          </AITitle>
          <AIDescription>
            Our proprietary AI technology combines the latest in machine learning with decades of sleep research to deliver results that generic sleep plans simply can't match.
          </AIDescription>
          
          <div className="row">
            <div className="col-md-4">
              <AIFeatureCard>
                <AIFeatureIcon>
                  <i className="bi bi-fingerprint"></i>
                </AIFeatureIcon>
                <AIFeatureTitle>Personalized to Your Child</AIFeatureTitle>
                <AIFeatureDescription>
                  Unlike generic sleep plans, our AI creates a unique strategy tailored to your child's temperament, sleep associations, and developmental stage.
                </AIFeatureDescription>
              </AIFeatureCard>
            </div>
            
            <div className="col-md-4">
              <AIFeatureCard>
                <AIFeatureIcon>
                  <i className="bi bi-graph-up"></i>
                </AIFeatureIcon>
                <AIFeatureTitle>Continuous Improvement</AIFeatureTitle>
                <AIFeatureDescription>
                  Our AI learns from successful sleep plans, constantly improving its recommendations based on what works best for children similar to yours.
                </AIFeatureDescription>
              </AIFeatureCard>
            </div>
            
            <div className="col-md-4">
              <AIFeatureCard>
                <AIFeatureIcon>
                  <i className="bi bi-clock-history"></i>
                </AIFeatureIcon>
                <AIFeatureTitle>Instant Plan Delivery</AIFeatureTitle>
                <AIFeatureDescription>
                  Get your personalized sleep plan instantly, without waiting days or weeks for a consultation. Start implementing better sleep habits right away.
                </AIFeatureDescription>
              </AIFeatureCard>
            </div>
          </div>
          
          <div className="row mt-4">
            <div className="col-md-4">
              <AIFeatureCard>
                <AIFeatureIcon>
                  <i className="bi bi-calendar-check"></i>
                </AIFeatureIcon>
                <AIFeatureTitle>Age-Appropriate Schedules</AIFeatureTitle>
                <AIFeatureDescription>
                  Our AI creates developmentally appropriate sleep schedules that align with your child's biological sleep windows for easier bedtimes.
                </AIFeatureDescription>
              </AIFeatureCard>
            </div>
            
            <div className="col-md-4">
              <AIFeatureCard>
                <AIFeatureIcon>
                  <i className="bi bi-lightbulb"></i>
                </AIFeatureIcon>
                <AIFeatureTitle>Smart Troubleshooting</AIFeatureTitle>
                <AIFeatureDescription>
                  When sleep challenges arise, our AI provides targeted solutions based on your specific situation, not generic advice.
                </AIFeatureDescription>
              </AIFeatureCard>
            </div>
            
            <div className="col-md-4">
              <AIFeatureCard>
                <AIFeatureIcon>
                  <i className="bi bi-heart-pulse"></i>
                </AIFeatureIcon>
                <AIFeatureTitle>Holistic Approach</AIFeatureTitle>
                <AIFeatureDescription>
                  Our AI considers all aspects of your child's life—from feeding patterns to family dynamics—to create a sleep plan that works for your whole family.
                </AIFeatureDescription>
              </AIFeatureCard>
            </div>
          </div>
        </div>
      </AISection>

      <CTASection>
        <div className="container">
          <CTATitle style={{ color: 'white' }}>
            Personalized Sleep Plans with <span>AI-Powered</span> Solutions
          </CTATitle>
          <CTADescription>
            Join numerous Australian families who've discovered the benefits of personalized AI sleep plans.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/signup" className="cta-button">
              Get Started Now
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default LandingPageAI;
